<template>
  <div class="flex flex-col gap-2">
    <ChecklistHeader
      :isCollapsed="isCollapsed"
      :itemsCount="itemsCount"
      :checkedItemsCount="checkedItemsCount"
      :canUpdateChecklist="canUpdateChecklist"
      :isTaskCompleted="isTaskCompleted"
      :busy="busy"
      @collapse="handleCollapse"
      @add:items="handleAddItems"
    />
    <div v-show="!isCollapsed" ref="checklistRef" class="flex flex-col gap-1">
      <ChecklistItem
        v-for="item in checklistItems"
        :key="item.id"
        :data-value="item.id"
        :item="item"
        :isEditMode="isEditMode === item.id"
        :isDeleteMode="isDeleteMode === item.id"
        :canUpdateChecklist="canUpdateChecklist"
        :currentUserId="currentUserId"
        :isAssignedToTask="isUserAssignedToTask"
        :isTaskCompleted="isTaskCompleted"
        :busy="busy"
        @update:description="description = $event"
        @update:checked="handleUpdateChecked(item, $event)"
        @open:actions="maxTablet && handleOpenActionsDrawer(item)"
      >
        <template #actions v-if="showActions">
          <ChecklistItemActions
            :item="item"
            :isEditMode="isEditMode"
            :isDeleteMode="isDeleteMode"
            :canUpdateChecklist="canUpdateChecklist"
            :canDeleteChecklist="canDeleteChecklist"
            :isTaskCompleted="isTaskCompleted"
            :busy="busy"
            @edit:mode="handleEditMode(item)"
            @delete:mode="handleDeleteMode(item)"
            @open:actions="handleOpenActionsDrawer(item)"
            @duplicate:item="handleDuplicateItem(item)"
            @update:description="handleUpdateDescription(item)"
            @delete:item="handleDeleteItem(item)"
          />
        </template>
      </ChecklistItem>
    </div>
    <Actions
      v-if="maxTablet"
      v-model:open="openActionsDrawer"
      :item="_item"
      :canUpdateChecklist="canUpdateChecklist"
      :canDeleteChecklist="canDeleteChecklist"
      @duplicate:item="handleDuplicateItem"
      @update:description="handleUpdateDescription(_item, $event)"
      @delete:item="handleDeleteItem"
      class="absolute"
    />
  </div>
</template>

<script setup>
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { usePage } from "@inertiajs/vue3";
import Sortable from "sortablejs";
import {
  Actions,
  ChecklistItem,
  ChecklistHeader,
  ChecklistItemActions,
} from "@/SharedComponents/Checklist";
import { useTaskChecklist } from "@/Composables/useTaskChecklist";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { maxTablet } = useBreakpoints();

const checklistRef = ref(null);
const sortableInstance = ref(null);

const props = defineProps({
  open: { type: Boolean, required: true },
  task: { type: Object, required: true },
});

const {
  checklist,
  busy,
  addItems,
  duplicateItem,
  checkItem,
  uncheckItem,
  updateItem,
  deleteItem,
} = useTaskChecklist(computed(() => props.task));

const checklistItems = computed(() => checklist.value?.items || []);
const description = ref("");

const isCollapsed = ref(false);
const isEditMode = ref(undefined);
const isDeleteMode = ref(undefined);

const _item = ref(null);
const openActionsDrawer = ref(false);

const itemsCount = computed(() => {
  return checklistItems.value.length;
});

const checkedItemsCount = computed(() => {
  return checklistItems.value.filter((item) => item.checked).length;
});

const { hasPermission, permissions } = inject("permissions");

const canUpdateChecklist = computed(() =>
  hasPermission(permissions.updateChecklist, props.task.tenant.id)
);

const canDeleteChecklist = computed(() =>
  hasPermission(permissions.deleteChecklist, props.task.tenant.id)
);

const currentUserId = computed(() => usePage().props.auth.user.id);

const isUserAssignedToTask = computed(() => {
  return (
    // user will be assigned to task if there are no assignees
    props.task.assignees?.length === 0 ||
    props.task.assignees?.some(
      (assignee) => assignee.id === currentUserId.value
    )
  );
});

const isTaskCompleted = computed(() => {
  return props.task.completed_at != null;
});

const showActions = computed(() => {
  return (
    (canUpdateChecklist.value || canDeleteChecklist.value) &&
    !isTaskCompleted.value
  );
});

function handleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}

function handleOpenActionsDrawer(item) {
  _item.value = item;
  openActionsDrawer.value = true;
}

function handleEditMode(item) {
  if (item.checked || isTaskCompleted.value) {
    return;
  }
  isDeleteMode.value = undefined;
  isEditMode.value = isEditMode.value === item.id ? undefined : item.id;
  description.value = isEditMode.value === item.id ? item.description : "";
}

function handleDeleteMode(item) {
  if (item.checked || isTaskCompleted.value) {
    return;
  }
  isEditMode.value = undefined;
  isDeleteMode.value = isDeleteMode.value === item.id ? undefined : item.id;
}

function handleAddItems(items) {
  if (isTaskCompleted.value) {
    return;
  }
  addItems({
    items: items.map((item, index) => ({
      id: "new_item_" + index,
      description: item,
    })),
  });
}

function handleDuplicateItem(item) {
  if (isTaskCompleted.value) {
    return;
  }
  duplicateItem({ item });
}

function handleUpdateChecked(item, check) {
  if (
    isTaskCompleted.value ||
    (item.checked_by?.id !== currentUserId.value &&
      !canUpdateChecklist.value &&
      !isUserAssignedToTask.value)
  ) {
    return;
  }

  if (check) {
    checkItem({ item });
  } else if (
    item.checked_by?.id === currentUserId.value ||
    canUpdateChecklist.value
  ) {
    uncheckItem({ item });
  }
}

async function handleUpdateItem(item, data) {
  if (isTaskCompleted.value) {
    return;
  }
  await updateItem({ item, data });
}

async function handleUpdateDescription(item, value = description.value) {
  if (isTaskCompleted.value) {
    return;
  }
  _item.value = null;
  await handleUpdateItem(item, { description: value });
  isEditMode.value = undefined;
  description.value = "";
}

async function handleDeleteItem(item) {
  if (isTaskCompleted.value) {
    return;
  }
  _item.value = null;
  await deleteItem({ item });
  isDeleteMode.value = undefined;
}

function initSortable() {
  if (checklistRef.value) {
    sortableInstance.value = new Sortable(checklistRef.value, {
      handle: ".drag-handle",
      animation: 150,
      onUpdate: function (event) {
        if (isTaskCompleted.value) {
          return;
        }
        const item = checklistItems.value.find(
          (item) => item.id === event.item.dataset.value
        );
        const newPosition = event.newIndex + 1;
        handleUpdateItem(item, { position: newPosition });
      },
    });
  }
}

function destroySortable() {
  if (sortableInstance.value) {
    sortableInstance.value.destroy();
    sortableInstance.value = null;
  }
}

onMounted(() => {
  initSortable();
});

onUnmounted(() => {
  destroySortable();
});

watch(checklistItems, () => {
  destroySortable();
  nextTick(() => {
    initSortable();
  });
});
</script>
